import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuote } from "@/context/QuoteContext";
import { CompanyWebsiteLink } from "@/components/CompanyWebsiteLink";

const easePremium = [0.76, 0, 0.24, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`line-mask ${className}`}>
      <motion.span
        initial={{ y: "108%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: easePremium }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.4, 0.75]);

  const { openQuote } = useQuote();
  const navigate = useNavigate();

  return (
    <section
      ref={containerRef}
      id="top"
      className="relative h-[100svh] w-full overflow-hidden bg-brand-green"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y: imageY, scale: imageScale }} className="absolute inset-0">
        <picture>
          <source media="(min-width: 768px)" srcSet="/pdf-images/page-01-hd-wide.jpg" />
          <img
            src="/pdf-images/page-01-mobile-hd.jpg"
            alt="Atelier chevron door"
            className="w-full h-full object-cover object-center"
            fetchPriority="high"
          />
        </picture>
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0"
        aria-hidden
      >
        <div className="absolute inset-0 bg-gradient-to-b from-brand-green/60 via-brand-green/30 to-brand-green/95" />
        {/* Left-side vignette to lift copy above the wood grain */}
        <div className="absolute inset-y-0 left-0 w-full md:w-3/5 bg-gradient-to-r from-brand-green/80 via-brand-green/40 to-transparent" />
      </motion.div>

      {/* Content — clean, Range Rover-style restrained layout */}
      <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mb-6"
        >
          <span
            className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-gold font-medium"
            data-testid="hero-eyebrow"
          >
            The Atelier Collection · European Design
          </span>
        </motion.div>

        <h1
          className="text-brand-bone font-light leading-[1.05] tracking-[-0.02em] max-w-3xl text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem]"
          data-testid="hero-title"
        >
          <span className="block">
            <MaskedLine delay={0.35}>Doors, engineered</MaskedLine>
          </span>
          <span className="block text-brand-gold">
            <MaskedLine delay={0.5}>for the ones you build for.</MaskedLine>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-6 max-w-md text-brand-bone/80 text-base md:text-lg font-light leading-relaxed"
        >
          European craftsmanship. Premium materials. Every door made to your measure —
          exterior, interior, and garage.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <button
            onClick={() => navigate("/collection/exterior")}
            className="btn-primary"
            data-testid="hero-configure-cta"
          >
            Configure Doors
            <span aria-hidden>→</span>
          </button>
          <a href="#exterior" className="btn-ghost" data-testid="hero-explore-cta">
            Explore Collections
          </a>
          <CompanyWebsiteLink
            placement="homepage"
            testId="hero-main-company-link"
            className="px-5 py-3 text-sm font-medium bg-brand-green/70"
          />
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute right-6 md:right-10 bottom-6 z-10 flex items-center gap-3 text-brand-bone/60"
      >
        <span className="text-[0.6rem] tracking-[0.28em] uppercase">Scroll</span>
        <span className="block w-10 h-px bg-brand-gold/50 overflow-hidden relative">
          <motion.span
            className="absolute inset-y-0 left-0 bg-brand-gold"
            initial={{ width: 0 }}
            animate={{ width: ["0%", "100%", "0%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
}
